import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { textStyle } from "../../styles/Style";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveLocationFieldErrors,
  reserveLocationFieldHelperText,
} from "../../../constants/reserveLocationValidationHelperText";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveRowFieldTooltip,
  reserveTablesTooltip,
} from "../../../constants/ReserveTooltipInformation";
import "../../styles/popupStyle.css";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import useScrollToTop from "../../hooks/useScrollToTop";

const ReserveTables = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [seatingArea, setSeatingArea] = useState<string | null>(null);
  const [seatingType, setSeatingType] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [seatingAreaError, setSeatingAreaError] = useState<boolean>(false);
  const [seatingTypeError, setSeatingTypeError] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tables, setTables] = useState<any[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const validateInput = () => {
    setSeatingAreaError(false);
    setSeatingTypeError(false);

    let ok = true;
    const textMax = validationLength.roomFeatureName;
    const seatingAreaTrim = (seatingArea ?? "").trim();
    const seatingTypeTrim = (seatingType ?? "").trim();

    if (!seatingAreaTrim) {
      setSeatingAreaError(true);
      ok = false;
    } else if (
      !validateString(seatingAreaTrim, textMax, validationType.SPACE)
    ) {
      setSeatingAreaError(true);
      ok = false;
    }

    if (!seatingTypeTrim) {
      setSeatingTypeError(true);
      ok = false;
    } else if (
      !validateString(seatingTypeTrim, textMax, validationType.SPACE)
    ) {
      setSeatingTypeError(true);
      ok = false;
    }

    if (!ok) {
      if (!seatingAreaTrim) {
        setAlertMessage("Seating area is mandatory");
      } else if (!seatingTypeTrim) {
        setAlertMessage("Seating type is mandatory");
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    setTables(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(tables);
    editClicked &&
      setSelectedId(checkData(tables, selectedId) ? selectedId : null);
  }, [tables]);

  const updateTable = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (tables ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        "Seating Area",
        seatingArea,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setSeatingAreaError(false);
    setSeatingTypeError(false);
    setTables((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (String(item.id) === String(selectedId)) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editTable(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.tables} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addTable = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(tables ?? [], "Seating Area", seatingArea)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setTables((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editTable(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  const editTable = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setSeatingArea(null);
      setSeatingType(null);
      setActive(true);
      setSeatingAreaError(false);
      setSeatingTypeError(false);
      setEditClicked(false);
      return;
    }
    setSeatingArea(
      list?.["Seating Area"] != null
        ? String(list["Seating Area"])
        : list?.Area != null
          ? String(list.Area)
          : null,
    );
    setSeatingType(
      list?.["Seating Type"] != null
        ? String(list["Seating Type"])
        : list?.["Location Type"] != null
          ? String(list["Location Type"])
          : null,
    );
    setActive(list?.Active ?? true);
    setSeatingAreaError(false);
    setSeatingTypeError(false);
  };

  const deleteTable = (id: number) => {
    setTables(deleteItem(tables ?? [], id));
  };

  const payload = {
    "Seating Area": seatingArea,
    "Seating Type": seatingType,
    Active: active,
  };

  const seatingAreaHelperText = seatingAreaError
    ? !(seatingArea?.trim() ?? "")
      ? reserveLocationFieldErrors.required
      : reserveLocationFieldErrors.area
    : reserveLocationFieldHelperText.area;

  const seatingTypeHelperText = seatingTypeError
    ? !(seatingType?.trim() ?? "")
      ? reserveLocationFieldErrors.required
      : reserveLocationFieldErrors.locationType
    : reserveLocationFieldHelperText.locationType;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.tables}
              <Tooltip title={tooltipComponent(reserveTablesTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editTable(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editTable(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Seating Area
              <Tooltip
                title={tooltipComponent(reserveTablesTooltip.seatingArea)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveTablesSeatingArea"
              variant="outlined"
              hiddenLabel
              placeholder="Seating Area"
              value={seatingArea ?? ""}
              helperText={seatingAreaHelperText}
              error={seatingAreaError}
              onChange={(e: any) => {
                setSeatingArea(e.target.value);
                setSeatingAreaError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Seating Area" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Seating Type
              <Tooltip
                title={tooltipComponent(reserveTablesTooltip.seatingType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveTablesSeatingType"
              variant="outlined"
              hiddenLabel
              placeholder="Seating Type"
              value={seatingType ?? ""}
              helperText={seatingTypeHelperText}
              error={seatingTypeError}
              onChange={(e: any) => {
                setSeatingType(e.target.value);
                setSeatingTypeError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Seating Type" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Active?
              <Tooltip title={tooltipComponent(reserveRowFieldTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack
              className="form-checkbox-row form-two-col-row"
              direction="row"
              alignItems="center"
              gap={1}
            >
              <Switch
                checked={active}
                onChange={() => setActive(!active)}
                inputProps={{ "aria-label": "Active table" }}
              />
              <Typography variant="body2" color="text.secondary">
                {active ? "Yes" : "No"}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() => (selectedId ? updateTable() : addTable())}
              id="addReserveTables"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.tables}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.tables} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(tables) && tables.length > 0 ? (
                <DraggableItemList
                  itemData={tables}
                  onOrderUpdate={(order) => setTables(order)}
                  onEdit={(info) => {
                    editTable(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteTable(id)}
                  code="Seating Area"
                  name="Seating Type"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.tables} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveTables;
